import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Fieldset from '../Fieldset/Fieldset';
import { useTextField } from 'react-aria';
import DatePart from './DatePart';
import moment from 'moment';

const DateInput = (props) => {
    const intl = useIntl();
    const [result, setResult] = useState({ day: '', month: '', year: '', valid: false });
    const [error, setError] = useState(null);

    const ref = useRef();
    const { inputProps, labelProps, errorMessageProps, descriptionProps } = useTextField(props, ref);

    useEffect(() => {
        if (props.errorMessage) {
            setError(props.errorMessage);
        } else {
            setError(null);
        }
    }, [props.errorMessage]);

    useEffect(() => {
        if (props.value) {
            const m = moment(props.value);
            setResult({ day: m.date(), month: m.month() + 1, year: m.year(), valid: m.isValid() });
        }
    }, [props.value]);

    function setResultsAndUpdateFormik (r) {
        setResult(r);
        props.onChange && props.onChange({ id: inputProps.id, name: props.name, value: r });
        props.onBlur && props.onBlur({ target: { id: inputProps.id, name: props.name } });
    }

    function handleDayBlur (event) {
        const r = validateDateValue({ ...result, day: event.target.value });
        setResultsAndUpdateFormik(r);
    }

    function handleDayChange (event) {
        setResult({ ...result, day: event.target.value });
    }

    function handleMonthBlur (event) {
        const r = validateDateValue({ ...result, month: event.target.value });
        setResultsAndUpdateFormik(r);
    }

    function handleMonthChange (event) {
        setResult({ ...result, month: event.target.value });
    }

    function handleYearBlur (event) {
        const r = validateDateValue({ ...result, year: event.target.value });
        setResultsAndUpdateFormik(r);
    }

    function handleYearChange (event) {
        setResult({ ...result, year: event.target.value });
    }

    function validateDateValue (dateValue) {
        if (dateValue.day && dateValue.month && dateValue.year) {
            const m = moment(`${dateValue.year}-${dateValue.month}-${dateValue.day}`, 'YYYY-M-D', true);
            const validDate = m.isValid();
            if (!validDate) {
                setError(intl.formatMessage({ id: 'DateInput.InvalidDateMessage' }).replace('{0}', props.label.toLowerCase()));
            } else {
                setError(props.errorMessage);
            }
            return { ...dateValue, valid: validDate };
        } else {
            return { ...dateValue, valid: false };
        }
    }

    return (
        <Fieldset label={props.label} labelProps={labelProps} labelAsTitle={props.labelAsTitle} errorMessage={error} errorMessageProps={errorMessageProps} description={props.description} descriptionProps={descriptionProps} data-testid={props['data-testid'] ? props['data-testid'] : null}>
            <div className="govuk-date-input">
                <DatePart
                    label={intl.formatMessage({ id: 'DateInput.Day' })}
                    value={result.day.toString()}
                    width="width-2"
                    hasError={!!(props.errorMessage)}
                    onBlur={handleDayBlur}
                    onChange={handleDayChange}
                    data-testid={props['data-testid'] ? props['data-testid'] + '-day-input' : null}
                    id={props.name}
                    name={props.name ? props.name + 'Day' : undefined}
                />
                <DatePart
                    label={intl.formatMessage({ id: 'DateInput.Month' })}
                    value={result.month.toString()}
                    width="width-2"
                    hasError={!!(props.errorMessage)}
                    onBlur={handleMonthBlur}
                    onChange={handleMonthChange}
                    data-testid={props['data-testid'] ? props['data-testid'] + '-month-input' : null}
                    name={props.name ? props.name + 'Month' : undefined}
                />
                <DatePart
                    label={intl.formatMessage({ id: 'DateInput.Year' })}
                    value={result.year.toString()}
                    width="width-4"
                    hasError={!!(props.errorMessage)}
                    onBlur={handleYearBlur}
                    onChange={handleYearChange}
                    data-testid={props['data-testid'] ? props['data-testid'] + '-year-input' : null}
                    name={props.name ? props.name + 'Year' : undefined}
                />
            </div>
        </Fieldset>
    );
};

DateInput.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.string,
    labelAsTitle: PropTypes.bool,
    description: PropTypes.string,
    value: PropTypes.instanceOf(Date),
    errorMessage: PropTypes.string,
    'data-testid': PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func
};

DateInput.defaultProps = {
    value: null
};

export default DateInput;
